import { chartTableFlatTransformer } from '../chartTableFlatTransformer';
import { getPercentage, getDynamicPercentage } from './tableUtils';

// Aggregate static-stage counts and per-stage times across all rows. Computed
// for both the aggregate path and the single-job path so the static-stage
// footer (Total row) always has its denominators — the PDF export's
// saved-report fetch carries a stage_mapping (`misc`) and would otherwise skip
// these, leaving the Total avg time-in-stage blank.
const computeStaticStageTotals = (data = []) => ({
  application_review_total_count_total: data?.reduce(
    (acc, curr) => acc + (curr?.application_review_total_count || 0),
    0
  ),
  interviews_total_count_total: data?.reduce(
    (acc, curr) => acc + (curr?.interviews_total_count || 0),
    0
  ),
  offer_total_count_total: data?.reduce(
    (acc, curr) => acc + (curr?.offer_total_count || 0),
    0
  ),
  hired_total_count_total: data?.reduce(
    (acc, curr) => acc + (curr?.hired_total_count || 0),
    0
  ),
  // Count-weighted average numerators: SUM(time_in_stage) per stage.
  time_in_application_review_total: data?.reduce(
    (acc, curr) => acc + (curr?.time_in_application_review || 0),
    0
  ),
  time_in_interviews_total: data?.reduce(
    (acc, curr) => acc + (curr?.time_in_interviews || 0),
    0
  ),
  time_in_offer_total: data?.reduce(
    (acc, curr) => acc + (curr?.time_in_offer || 0),
    0
  ),
});

// Per-row static-stage fields: the shared totals plus each stage's
// passthrough/overall percentage relative to the column total.
const withStaticStageTotals = (datum, totals) => ({
  application_review_total_count_total:
    totals.application_review_total_count_total,
  application_review_total_count_per: getPercentage(
    datum?.application_review_total_count,
    totals.application_review_total_count_total
  ),
  interviews_total_count_total: totals.interviews_total_count_total,
  interviews_total_count_total_per: getPercentage(
    datum?.interviews_total_count,
    totals.interviews_total_count_total
  ),
  offer_total_count_total: totals.offer_total_count_total,
  offer_total_count_per: getPercentage(
    datum?.offer_total_count,
    totals.offer_total_count_total
  ),
  hired_total_count_total: totals.hired_total_count_total,
  hired_total_count_per: getPercentage(
    datum?.hired_total_count,
    totals.hired_total_count_total
  ),
  time_in_application_review_total: totals.time_in_application_review_total,
  time_in_interviews_total: totals.time_in_interviews_total,
  time_in_offer_total: totals.time_in_offer_total,
});

const singleJobTableWithTotal = ({ data = [] }) => {
  const initialTotal = Array(15).fill(0);

  const dynamic_total = data?.reduce((acc, curr) => {
    for (let i = 0; i < initialTotal?.length; i++) {
      acc[i] = acc[i] + curr[`s${i + 1}_total_count`];
    }
    return acc;
  }, initialTotal);

  // Static-stage totals are also attached here (see helper) so the static-stage
  // footer still works when the response carries a stage_mapping (`misc`) and
  // routes the data through this path — e.g. the PDF export's saved-report fetch.
  const staticTotals = computeStaticStageTotals(data);

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      const percentage = getDynamicPercentage(datum, dynamic_total);
      return {
        ...datum,
        dynamic_total,
        ...withStaticStageTotals(datum, staticTotals),
        ...percentage,
        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const tableWithTotal = ({ data = [], misc }) => {
  if (misc) {
    return singleJobTableWithTotal({ data });
  }

  const staticTotals = computeStaticStageTotals(data);

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => ({
      ...datum,
      ...withStaticStageTotals(datum, staticTotals),
      subRows: innerFunc(datum?.subRows),
    }));
  }

  return innerFunc(data);
};

export const pipelineTableTransformer = ({ response, args }) => {
  // Safely extract properties from response
  const { chart = [], headers, lookups, misc, table = [] } = response || {};
  const { dt, g1, g2 } = lookups || {}; // Lookup mappings for data transformation

  const data = g1?.length || g2?.length ? table : chart;
  const headerKey = g1?.length ? 'g1' : 'dt';

  const { group_by_1, group_by_2 } = args || {};

  // Transform raw chart data into a flat structure
  const transformedData = chartTableFlatTransformer({
    headerKey,
    headers,
    data,
    g1,
    g2,
    dt,
    g1Key: group_by_1?.id || group_by_1,
    g2Key: group_by_2?.id || group_by_2,
  });

  const tableData = tableWithTotal({ data: transformedData, misc });

  return tableData;
};
